/**
 * @description Hooks for managing connections and credentials
 * This file contains hooks for creating credentials and updating tenant settings
 * when adding new connections.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAppStore } from '@/store/appStore';
import {
  createNewCredential,
  patchCredential,
  fetchAllCredential,
  getTenantDetails,
  updateTenantSettings,
  createDataset,
  deleteDataset
} from '@/services/DataService';
import { logger } from "@/lib/logger";

import { OauthCredentials, TenantSettings, CreateConnectionParams, ReauthorizeConnectionParams } from '@/types/connections';

// Hook to get tenant details
export const useTenantDetails = (options?: { enabled?: boolean }) => {
  const { isSignedIn } = useAuth();
  const enabled = Boolean(isSignedIn && (options?.enabled ?? true));

  return useQuery({
    queryKey: ['tenant-details'],
    queryFn: getTenantDetails,
    enabled,
    // Override global setting to refetch when window regains focus
    // Critical for updating UI state after user returns from idle
    refetchOnWindowFocus: true,
  });
};

// Hook to fetch credentials
export const useCredentials = () => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['credentials'],
    queryFn: fetchAllCredential,
    enabled: isSignedIn,
  });
};

// Hook to create credential
export const useCreateCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: OauthCredentials) => createNewCredential(payload),
    onSuccess: () => {
      // Invalidate both credentials and tenant details to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Hook to update tenant settings
export const useUpdateTenantSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: TenantSettings) => updateTenantSettings(settings),
    onSuccess: () => {
      // Invalidate both credentials and tenant details to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Combined hook to create connection (credential + tenant settings update)
export const useCreateConnection = () => {
  const createCredentialMutation = useCreateCredential();
  const updateTenantSettingsMutation = useUpdateTenantSettings();
  const { data: tenantData } = useTenantDetails();

  return useMutation({
    mutationFn: async ({ oauthPayload, appName }: CreateConnectionParams) => {
      // Step 1: Create credential
      const credentialResponse = await createCredentialMutation.mutateAsync({
        credential: oauthPayload
      });

      // Step 2: Update tenant settings with new credential
      const currentSettings = tenantData?.tenant?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        [appName]: {
          credential_id: credentialResponse.credential.id
        }
      };

      await updateTenantSettingsMutation.mutateAsync(updatedSettings);

      return {
        credential: credentialResponse.credential,
        settings: updatedSettings
      };
    },
  });
};

// Hook to patch credential
export const usePatchCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ credentialId, payload }: { credentialId: string; payload: OauthCredentials }) => 
      patchCredential(credentialId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Combined hook to reauthorize connection (patch credential + tenant settings update)
export const useReauthorizeConnection = () => {
  const patchCredentialMutation = usePatchCredential();
  const updateTenantSettingsMutation = useUpdateTenantSettings();
  const { data: tenantData } = useTenantDetails();

  return useMutation({
    mutationFn: async ({ oauthPayload, credentialId }: ReauthorizeConnectionParams) => {
      // Step 1: Patch credential
      const credentialResponse = await patchCredentialMutation.mutateAsync({
        credentialId,
        payload: { credential: oauthPayload }
      });

      // Step 2: Update tenant settings (ensure the credential_id is set in settings)
      const currentSettings = tenantData?.tenant?.settings || {};
      const appName = oauthPayload.app_name;
      const updatedSettings = {
        ...currentSettings,
        [appName]: {
          credential_id: credentialId
        }
      };

      await updateTenantSettingsMutation.mutateAsync(updatedSettings);

      return {
        credential: credentialResponse.credential,
        settings: updatedSettings
      };
    },
  });
};

// Direct reauthorization hook - handles OAuth flow and credential patching without modal
// This is exclusively done to avoid 2 click actions for reauthorizing a connection
export const useDirectReauthorization = () => {
  const reauthorizeConnectionMutation = useReauthorizeConnection();
  const accessToken = useAppStore((state) => state.authDetails.accessToken);
  const refreshToken = useAppStore((state) => state.authDetails.refreshToken);
  const userId = useAppStore((state) => state.authDetails.userId);
  const userName = useAppStore((state) => state.authDetails.userName);
  const userEmail = useAppStore((state) => state.authDetails.userEmail);
  const setAuthDetails = useAppStore((state) => state.setAuthDetails);

  const prepareOAuthPayload = (appName: string, displayName: string) => ({
    app_name: appName,
    display_name: displayName,
    auth_name: "default",
    token_type: "oauth",
    access_token: accessToken as string,
    refresh_token: refreshToken as string,
    principal_id: userId as string,
    principal_name: userName as string,
    principal_email: userEmail as string,
  });

  const resetAuthDetails = () => {
    setAuthDetails({
      userId: '',
      userName: '',
      userEmail: '',
      accessToken: '',
      refreshToken: '',
    });
  };

  const handleReauthorization = async ({ credentialId, appName, displayName }: { credentialId: string; appName: string; displayName: string }) => {
    try {
      const oauthPayload = prepareOAuthPayload(appName, displayName);
      await reauthorizeConnectionMutation.mutateAsync({
        oauthPayload,
        credentialId
      });
    } catch (error) {
      logger(error as Error, { context: 'Error handling reauthorization' });
      throw error;
    } finally {
      resetAuthDetails();
    }
  };

  return {
    reauthorize: handleReauthorization,
    isPending: reauthorizeConnectionMutation.isPending,
  };
};

/**
 * Hook to create a dataset for a credential.
 */
export const useCreateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      credentialId,
      displayName,
    }: {
      credentialId: string;
      displayName: string;
    }) => {
      return createDataset(credentialId, displayName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['credentials'],
      });
    },
  });
};

/**
 * Hook to delete a dataset for a credential.
 */
export const useDeleteDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      credentialId,
      datasetId,
    }: {
      credentialId: string;
      datasetId: string;
    }) => {
      return deleteDataset(credentialId, datasetId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['credentials'],
      });
    },
  });
};
